/**
 * Weather API module for displaying current weather conditions
 * Uses weatherapi.com service with session storage caching
 */

// Configuration
const WEATHER_CONFIG = {
    apiKey: import.meta.env.VITE_WEATHER_API_KEY,
    location: '38117',
    apiUrl: 'https://api.weatherapi.com/v1/current.json',
    cacheKey: 'weatherContent',
    iconBasePath: '/dist/weather-icons/'
};

// Weather condition code to icon mapping
const WEATHER_ICONS = {
    1000: 'sunny.svg',
    1002: 'moon.svg',
    1003: 'partly-sunny.svg',
    1004: 'cloudy-night.svg',
    1006: 'cloud.svg',
    1009: 'cloud.svg',
    1030: 'rainy.svg',
    1063: 'rainy.svg',
    1066: 'snow.svg',
    1069: 'snow.svg',
    1072: 'snow.svg',
    1087: 'thunderstorm.svg',
    1114: 'snow.svg',
    1117: 'snow.svg',
    1135: 'cloud.svg',
    1147: 'cloud.svg',
    1150: 'rainy.svg',
    1153: 'rainy.svg',
    1168: 'rainy.svg',
    1171: 'rainy.svg',
    1180: 'rainy.svg',
    1183: 'rainy.svg',
    1186: 'rainy.svg',
    1189: 'rainy.svg',
    1192: 'rainy.svg',
    1195: 'rainy.svg',
    1198: 'rainy.svg',
    1201: 'snow.svg',
    1204: 'snow.svg',
    1207: 'snow.svg',
    1210: 'snow.svg',
    1213: 'snow.svg',
    1216: 'snow.svg',
    1219: 'snow.svg',
    1222: 'snow.svg',
    1225: 'snow.svg',
    1237: 'snow.svg',
    1240: 'rainy.svg',
    1243: 'rainy.svg',
    1246: 'rainy.svg',
    1249: 'snow.svg',
    1252: 'snow.svg',
    1255: 'snow.svg',
    1258: 'snow.svg',
    1261: 'snow.svg',
    1264: 'snow.svg',
    1273: 'thunderstorm.svg',
    1276: 'thunderstorm.svg',
    1279: 'snow.svg',
    1282: 'snow.svg'
};

/**
 * Get the appropriate icon for weather condition and time of day
 */
function getWeatherIcon(conditionCode, isDay) {
    // Handle special night cases
    if (conditionCode === 1000 && isDay === 0) {
        return WEATHER_ICONS[1002]; // moon
    }
    if (conditionCode === 1003 && isDay === 0) {
        return WEATHER_ICONS[1004]; // cloudy-night
    }

    // Default to condition code mapping
    return WEATHER_ICONS[conditionCode] || WEATHER_ICONS[1006]; // fallback to cloud
}

/**
 * Fetch and return SVG content as inline HTML
 */
async function fetchWeatherIconSvg(conditionCode, isDay, conditionText) {
    const iconPath = `${WEATHER_CONFIG.iconBasePath}${getWeatherIcon(conditionCode, isDay)}`;
    try {
        const response = await fetch(iconPath);
        if (!response.ok) {
            throw new Error(`Failed to fetch icon: ${response.status}`);
        }
        let svgContent = await response.text();
        // Add aria-label for accessibility
        svgContent = svgContent.replace('<svg', `<svg aria-label="${conditionText}"`);
        return svgContent;
    } catch (error) {
        console.error('Failed to fetch weather icon:', error);
        return '';
    }
}

/**
 * Build API URL with parameters
 */
function buildApiUrl() {
    const params = new URLSearchParams({
        key: WEATHER_CONFIG.apiKey,
        q: WEATHER_CONFIG.location,
        aqi: 'no'
    });
    return `${WEATHER_CONFIG.apiUrl}?${params}`;
}

/**
 * Fetch weather data from API
 */
async function fetchWeatherData() {
    try {
        const response = await fetch(buildApiUrl());
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Failed to fetch weather data:', error);
        throw error;
    }
}

/**
 * Process weather data and create HTML content
 */
async function processWeatherData(data) {
    const temperature = Math.round(data.current.temp_f);
    const weatherIconSvg = await fetchWeatherIconSvg(
        data.current.condition.code,
        data.current.is_day,
        data.current.condition.text
    );

    return `${temperature}° <span class="block w-[15px] h-[15px] ml-p5">${weatherIconSvg}</span>`;
}

/**
 * Update weather containers with content
 */
function updateWeatherContainers(content) {
    const containers = document.querySelectorAll('.weather-container');
    containers.forEach(container => {
        container.innerHTML = content;
    });
}

/**
 * Get cached weather content
 */
function getCachedWeather() {
    return sessionStorage.getItem(WEATHER_CONFIG.cacheKey);
}

/**
 * Cache weather content
 */
function setCachedWeather(content) {
    sessionStorage.setItem(WEATHER_CONFIG.cacheKey, content);
}

/**
 * Initialize weather display
 */
async function initWeather() {
    const cachedContent = getCachedWeather();

    if (cachedContent) {
        updateWeatherContainers(cachedContent);
        return;
    }

    try {
        const weatherData = await fetchWeatherData();
        const content = await processWeatherData(weatherData);

        updateWeatherContainers(content);
        setCachedWeather(content);
    } catch (error) {
        console.error('Weather initialization failed:', error);
        // Optionally show fallback content
        updateWeatherContainers('Weather unavailable');
    }
}

export default initWeather;